// apps/smart-shikka-web/src/surfaces/botsSurface.tsx
// FNL-01 — custom assistant/bot library surface backed by the canonical
// /api/assistants authority. Create → edit draft → publish → rollback →
// deterministic preview → tombstone. No fake data: unavailable backend shows
// the truthful state.
import { useCallback, useState } from "react";
import { productText, useBrandColors } from "@smart-shikka/react";
import { useAuth } from "../auth.js";
import { useLocale } from "../ui.js";
import {
  EmptyState,
  SurfacePage,
  SurfaceStateView,
  surfaceGetJson,
  surfaceHeaders,
  useSurfaceQuery,
  type SurfaceQueryState,
} from "./surfaceScaffold.js";

interface AssistantRow {
  assistantId: string;
  name: string;
  description: string;
  visibility: string;
  status: string;
  publishedVersion: number | null;
}

interface PreviewData {
  boundVersion: number | null;
  providerCallMade: boolean;
  sideEffects: boolean;
  composedPrompt: string;
  declaredToolScopes: string[];
}

const BASE = () =>
  ((import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL) ??
  window.location.origin;

async function callApi(
  token: string,
  method: string,
  path: string,
  body?: Record<string, unknown>,
): Promise<{ ok: boolean; data: Record<string, unknown> | null }> {
  const r = await fetch(`${BASE()}${path}`, {
    method,
    headers: surfaceHeaders(token),
    credentials: "include",
    body: body === undefined ? null : JSON.stringify(body),
  }).catch(() => null);
  if (!r) return { ok: false, data: null };
  const data = (await r.json().catch(() => null)) as Record<string, unknown> | null;
  return { ok: r.ok, data };
}

export function BotsSurface() {
  const C = useBrandColors();
  const { token } = useAuth();
  const { locale } = useLocale();
  const hi = locale === "hi";
  const q = useSurfaceQuery<AssistantRow[]>("/api/assistants", (d) =>
    (Array.isArray(d.assistants) ? (d.assistants as Record<string, unknown>[]) : []).map((a) => ({
      assistantId: String(a.assistantId ?? ""),
      name: String(a.name ?? ""),
      description: String(a.description ?? ""),
      visibility: String(a.visibility ?? ""),
      status: String(a.status ?? ""),
      publishedVersion: typeof a.publishedVersion === "number" ? a.publishedVersion : null,
    })),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ id: string; data: PreviewData } | null>(null);
  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState("");
  const [shared, setShared] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [versions, setVersions] = useState<Record<string, number[]>>({});

  const refresh = q.retry;
  const act = useCallback(
    async (method: string, path: string, body?: Record<string, unknown>) => {
      if (!token) return;
      setBusy(true);
      setErr(null);
      const r = await callApi(token, method, path, body);
      setBusy(false);
      if (!r.ok) {
        setErr(String((r.data as { error?: string } | null)?.error ?? `HTTP ${method} failed`));
        return;
      }
      refresh();
    },
    [token, refresh],
  );

  const loadVersions = useCallback(
    async (id: string) => {
      if (!token) return;
      const r = await surfaceGetJson(token, `/api/assistants/${encodeURIComponent(id)}/versions`);
      if (r.ok && r.data && Array.isArray(r.data.versions)) {
        setVersions((v) => ({
          ...v,
          [id]: (r.data!.versions as Record<string, unknown>[]).map((x) => Number(x.version)),
        }));
      }
    },
    [token],
  );

  const doPreview = useCallback(
    async (id: string) => {
      if (!token) return;
      const r = await callApi(token, "POST", `/api/assistants/${encodeURIComponent(id)}/preview`);
      const p = (r.data as { preview?: Record<string, unknown> } | null)?.preview;
      if (r.ok && p) {
        setPreview({
          id,
          data: {
            boundVersion: typeof p.boundVersion === "number" ? p.boundVersion : null,
            providerCallMade: p.providerCallMade === true,
            sideEffects: p.sideEffects === true,
            composedPrompt: String(p.composedPrompt ?? ""),
            declaredToolScopes: Array.isArray(p.declaredToolScopes)
              ? (p.declaredToolScopes as unknown[]).map(String)
              : [],
          },
        });
      } else setErr(String((r.data as { error?: string } | null)?.error ?? "preview failed"));
    },
    [token],
  );

  const btn = (label: string, onClick: () => void, disabled = false) => (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      style={{
        fontSize: 11,
        padding: "4px 10px",
        borderRadius: 8,
        border: `1px solid ${C.border}`,
        background: C.card,
        color: C.fg,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );

  return (
    <SurfacePage
      route="bots"
      title={productText("navBots", locale)}
      subtitle={hi ? "Custom assistants — create, version, share और safe preview" : "Custom assistants — create, version, share, safe preview"}
    >
      {err && (
        <div role="status" style={{ fontSize: 11, color: "#b91c1c", marginBottom: 8 }}>{err}</div>
      )}
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, background: C.card, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: C.fg, marginBottom: 8 }}>{hi ? "नया assistant" : "New assistant"}</div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={hi ? "नाम" : "Name"}
          style={{ width: "100%", fontSize: 12, padding: "6px 8px", marginBottom: 6, border: `1px solid ${C.border}`, borderRadius: 8 }}
        />
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder={hi ? "Instructions (जैसे: हिंदी में जवाब दो)" : "Instructions"}
          rows={3}
          style={{ width: "100%", fontSize: 12, padding: "6px 8px", marginBottom: 6, border: `1px solid ${C.border}`, borderRadius: 8 }}
        />
        <label style={{ fontSize: 11, color: C.muted, display: "flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
          <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
          {hi ? "tenant_shared — पूरे tenant को दिखे" : "tenant_shared — visible to whole tenant"}
        </label>
        {btn(hi ? "Draft बनाएँ" : "Create draft", async () => {
          await act("POST", "/api/assistants", {
            name,
            instructions,
            visibility: shared ? "tenant_shared" : "private",
          });
          setName("");
          setInstructions("");
        }, !name.trim() || !instructions.trim())}
      </div>

      {q.kind !== "ready" ? (
        <SurfaceStateView state={q as SurfaceQueryState<unknown>} onRetry={q.retry} />
      ) : q.data.length === 0 ? (
        <EmptyState />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
          {q.data.map((a) => (
            <div key={a.assistantId} style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: "10px 14px", background: C.card }}>
              <div style={{ fontSize: 13, color: C.fg, fontWeight: 600 }}>{a.name}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                {a.status} · {a.visibility}
                {a.publishedVersion !== null ? ` · v${a.publishedVersion}` : ""}
              </div>
              {editId === a.assistantId ? (
                <div style={{ marginTop: 8 }}>
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={3}
                    style={{ width: "100%", fontSize: 12, padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: 8 }}
                  />
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    {btn(hi ? "सहेजें" : "Save", async () => {
                      await act("PATCH", `/api/assistants/${encodeURIComponent(a.assistantId)}`, {
                        name: a.name,
                        instructions: editText,
                        visibility: a.visibility,
                      });
                      setEditId(null);
                    })}
                    {btn(hi ? "रद्द" : "Cancel", () => setEditId(null))}
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                  {btn(hi ? "Edit" : "Edit", () => { setEditId(a.assistantId); setEditText(""); })}
                  {btn(hi ? "Publish" : "Publish", async () => {
                    await act("POST", `/api/assistants/${encodeURIComponent(a.assistantId)}/publish`, {});
                  })}
                  {btn(hi ? "Versions" : "Versions", () => void loadVersions(a.assistantId))}
                  {btn(hi ? "Preview" : "Preview", () => void doPreview(a.assistantId))}
                  {btn(hi ? "हटाएँ" : "Tombstone", async () => {
                    await act("POST", `/api/assistants/${encodeURIComponent(a.assistantId)}/tombstone`, {});
                  })}
                </div>
              )}
              {versions[a.assistantId]?.map((v) => (
                <div key={v} style={{ fontSize: 10, color: C.muted, marginTop: 6 }}>
                  v{v} {v !== a.publishedVersion && btn(hi ? "rollback" : "rollback", async () => {
                    await act("POST", `/api/assistants/${encodeURIComponent(a.assistantId)}/rollback`, { version: v });
                  })}
                </div>
              ))}
              {preview?.id === a.assistantId && (
                <div style={{ marginTop: 8, fontSize: 10, color: C.muted, borderTop: `1px solid ${C.border}`, paddingTop: 6 }}>
                  <div>
                    {hi ? "बाध्य version" : "bound version"}: {preview.data.boundVersion ?? "—"} · providerCallMade:{" "}
                    {String(preview.data.providerCallMade)} · sideEffects: {String(preview.data.sideEffects)}
                  </div>
                  <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{preview.data.composedPrompt.slice(0, 400)}</div>
                  <div style={{ marginTop: 4 }}>tools: {preview.data.declaredToolScopes.join(", ") || "—"}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </SurfacePage>
  );
}
